// A participant's file: everything about one person in aged care on one page, the way a care
// coordinator thinks about them. Before this a participant was spread over nine screens (funding,
// budgets, care plans, care management, statements, incidents, complaints, leave, supporters), and
// most of those showed nothing until the person was picked again from a dropdown.
//
// The header says who they are and what they are funded by; the overview answers the questions a
// family member asks on the phone (how much is left this quarter, when is the next visit, did the
// statement go out) and lists what needs doing for them. Every other tab is the existing screen,
// opened on this participant.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Pencil, CalendarPlus, HeartHandshake, Wallet, ClipboardList, CalendarDays, Receipt, ShieldAlert,
  Users, LayoutDashboard, Phone, Mail, MapPin, Cake, AlertCircle, Clock, CheckCircle2, ChevronRight, FileText,
} from 'lucide-react';
import { PersonAvatar, Tabs } from '../../components/ui';
import { CopyButton } from '../PlanManagement/ui';
import { Panel, AcStatus, Deadline, DetailDrawer, Field, FormRow, fmtDate, money, monthLabel, CATEGORY_HUE, ErrorState } from './design';
import { FundingDetail } from './Funding';
import AgedCareCarePlans from './CarePlans';
import AgedCareSahBudgets from './SahBudgets';
import AgedCareCareManagement from './CareManagement';
import AgedCareStatements from './Statements';
import AgedCareSirs from './Sirs';
import AgedCareComplaints from './Complaints';
import AgedCareLeave from './Leave';

const SECTIONS = ['overview', 'funding', 'careplan', 'visits', 'money', 'caremgmt', 'safety', 'people'];
const STATE = {
  over: { word: 'Over budget', chip: 'bg-red-50 text-red-800 ring-red-100' },
  fast: { word: 'Spending ahead of the quarter', chip: 'bg-amber-50 text-amber-800 ring-amber-100' },
  slow: { word: 'Underused', chip: 'bg-amber-50 text-amber-800 ring-amber-100' },
  on_track: { word: 'On track', chip: 'bg-emerald-50 text-emerald-800 ring-emerald-100' },
};
const SCHEME = { SAH: 'Support at Home', HCP: 'Home Care Package' };
const PATHWAY = { end_of_life: 'End-of-Life Pathway', restorative: 'Restorative Care Pathway' };
const ageOf = (dob) => {
  if (!dob) return null;
  const d = new Date(`${dob}T00:00:00`); const n = new Date();
  let a = n.getFullYear() - d.getFullYear();
  if (n.getMonth() < d.getMonth() || (n.getMonth() === d.getMonth() && n.getDate() < d.getDate())) a -= 1;
  return a;
};
const MARK = { overdue: [AlertCircle, 'text-red-700'], soon: [Clock, 'text-amber-700'] };

// The section a stored reminder belongs to, so "needs doing" opens the right tab of the file.
const TAB_OF = { funding: 'funding', budgets: 'money', claims: 'money', statements: 'money', careplans: 'careplan', caremgmt: 'caremgmt', sirs: 'safety', complaints: 'safety', referrals: 'funding', leave: 'visits' };

export default function ParticipantFile({ clientId, section, onSection, onGoTo }) {
  const [file, setFile] = useState(null);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const tab = SECTIONS.includes(section) ? section : 'overview';

  const load = useCallback(async () => {
    setError(null);
    try {
      const [f, plans] = await Promise.all([
        api.get(`/aged-care/participants/${clientId}/file`),
        api.get('/aged-care/funding').catch(() => ({ data: [] })),
      ]);
      setFile(f.data);
      const mine = (Array.isArray(plans.data) ? plans.data : []).filter((p) => String(p.client_id) === String(clientId));
      setPlan(mine[0] || null);
    } catch (e) { setError(e.response?.data?.message || 'Could not load the participant'); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="py-6"><ErrorState message={error} onRetry={load} /></div>;
  if (!file) return <FileSkeleton />;
  const c = file.client;
  const scheme = plan ? (SCHEME[String(plan.scheme || '').toUpperCase()] || plan.scheme) : null;
  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'funding', label: 'Funding', icon: Wallet },
    { id: 'careplan', label: 'Care plan', icon: ClipboardList },
    { id: 'visits', label: 'Visits', icon: CalendarDays },
    { id: 'money', label: 'Budgets and statements', icon: Receipt },
    { id: 'caremgmt', label: 'Care management', icon: HeartHandshake },
    { id: 'safety', label: 'Incidents and complaints', icon: ShieldAlert, count: (file.safety.sirs + file.safety.complaints) || undefined },
    { id: 'people', label: 'People', icon: Users },
  ];

  return (
    <div>
      <button type="button" onClick={() => onGoTo?.('participants')} className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-800 mb-3">
        <ArrowLeft size={14} /> Participants
      </button>

      {/* ── Who they are ── */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden mb-4">
        <div className="hue-bar h-[3px]" aria-hidden="true" />
        <div className="px-5 py-4 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <PersonAvatar id={c.id} firstName={c.first_name} lastName={c.last_name} size="lg" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display text-[24px] leading-tight font-semibold tracking-tight text-slate-900">
                  {c.preferred_name ? `${c.preferred_name} ${c.last_name}` : c.name}
                </h1>
                {c.is_active === false ? <AcStatus status="exited" /> : <AcStatus status="active" />}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {scheme && <Chip>{scheme}{plan.level ? `, ${String(plan.scheme).toUpperCase() === 'HCP' ? 'level' : 'classification'} ${plan.level}` : ''}</Chip>}
                {plan && plan.is_interim && <Chip tone="amber">Interim funding {plan.interim_pct ? `${Number(plan.interim_pct)}%` : ''}</Chip>}
                {plan && plan.no_worse_off && <Chip tone="violet">No worse off</Chip>}
                {file.chsp && <Chip>CHSP</Chip>}
                {file.pathways.filter((p) => !p.end_date || p.end_date >= file.today).map((p) => <Chip key={p.id} tone="violet">{PATHWAY[p.type] || p.type}</Chip>)}
              </div>
              <dl className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px] text-slate-600">
                <Ident label="My Aged Care" value={c.mac} />
                <Ident label="Services Australia" value={c.sa_id} missing="Not recorded: visits cannot be claimed" />
                {c.date_of_birth && <span className="inline-flex items-center gap-1"><Cake size={13} className="text-slate-400" />{fmtDate(c.date_of_birth)}{ageOf(c.date_of_birth) != null ? `, ${ageOf(c.date_of_birth)}` : ''}</span>}
                {(c.suburb || c.address) && <span className="inline-flex items-center gap-1"><MapPin size={13} className="text-slate-400" />{[c.suburb, c.state].filter(Boolean).join(' ') || c.address}</span>}
              </dl>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:shrink-0 lg:justify-end">
            <button type="button" className="btn-secondary btn-sm" onClick={() => setEditing(true)}><Pencil size={14} /> Edit details</button>
            <Link to="/scheduler" className="btn-secondary btn-sm"><CalendarPlus size={14} /> Book a visit</Link>
            <button type="button" className="btn-primary btn-sm" onClick={() => onSection('caremgmt')}><HeartHandshake size={14} /> Log care management</button>
          </div>
        </div>
        <div className="px-3">
          <Tabs tabs={tabs} value={tab} onChange={onSection} ariaLabel="Participant file" />
        </div>
      </div>

      {tab === 'overview' && <Overview file={file} plan={plan} onSection={onSection} onGoTo={onGoTo} />}
      {tab === 'funding' && (plan
        ? <FundingDetail plan={plan} onSaved={load} sections="funding" />
        : <NoPlan onGoTo={onGoTo} />)}
      {tab === 'careplan' && <AgedCareCarePlans clientId={clientId} embedded />}
      {tab === 'visits' && <Visits file={file} clientId={clientId} />}
      {tab === 'money' && (
        <div className="space-y-6">
          <AgedCareSahBudgets clientId={clientId} embedded />
          <AgedCareStatements clientId={clientId} embedded />
        </div>
      )}
      {tab === 'caremgmt' && <AgedCareCareManagement clientId={clientId} embedded />}
      {tab === 'safety' && (
        <div className="space-y-6">
          <AgedCareSirs clientId={clientId} embedded />
          <AgedCareComplaints clientId={clientId} embedded />
        </div>
      )}
      {tab === 'people' && (plan
        ? <FundingDetail plan={plan} onSaved={load} sections="people" />
        : <NoPlan onGoTo={onGoTo} />)}

      {editing && <EditDetails client={c} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
    </div>
  );
}

function Chip({ children, tone = 'teal' }) {
  const T = {
    teal: 'hue-chip ring-0',
    amber: 'bg-amber-50 text-amber-800 ring-1 ring-amber-100',
    violet: 'bg-violet-50 text-violet-800 ring-1 ring-violet-100',
  };
  return <span className={clsx('inline-flex items-center rounded-md px-2 py-0.5 text-[12px] font-medium', T[tone] || T.teal)}>{children}</span>;
}
function Ident({ label, value, missing }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-slate-400">{label}</span>
      {value
        ? <><span className="font-mono text-[12px] text-slate-700">{value}</span><CopyButton value={value} ariaLabel={`Copy ${label} ID`} size={12} /></>
        : <span className="text-amber-700">{missing || 'Not recorded'}</span>}
    </span>
  );
}

function FileSkeleton() {
  return (
    <div className="animate-pulse motion-reduce:animate-none" aria-busy="true" aria-label="Loading the participant">
      <div className="h-4 w-24 rounded bg-slate-100 mb-3" />
      <div className="rounded-xl border border-slate-200 bg-white p-5 mb-4">
        <div className="flex gap-4"><div className="w-12 h-12 rounded-full bg-slate-100" /><div className="flex-1 space-y-2"><div className="h-6 w-56 rounded bg-slate-100" /><div className="h-4 w-80 rounded bg-slate-100" /></div></div>
      </div>
      <div className="grid lg:grid-cols-3 gap-4"><div className="lg:col-span-2 h-56 rounded-xl bg-slate-100" /><div className="h-56 rounded-xl bg-slate-100" /></div>
    </div>
  );
}

function NoPlan({ onGoTo }) {
  return (
    <Panel bodyClass="px-5 py-8 text-center">
      <Wallet size={22} className="mx-auto text-slate-400" />
      <p className="mt-2 font-medium text-slate-800">No funding plan yet</p>
      <p className="text-[13px] text-slate-500 mt-1">Record their Notice of Decision as a funding plan so their visits can be claimed and their statements issued.</p>
      <button type="button" className="btn-primary btn-sm mt-3" onClick={() => onGoTo?.('funding')}>Open funding plans</button>
    </Panel>
  );
}

// ── Overview ───────────────────────────────────────────────────────────────────────────────────
function Overview({ file, plan, onSection }) {
  const q = file.quarter;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
      <div className="lg:col-span-2 space-y-4">
        {q ? <QuarterMoney q={q} onOpen={() => onSection('money')} /> : (
          <Panel title="This quarter" icon={Wallet} bodyClass="px-4 py-4 text-[13px] text-slate-600">
            {plan ? 'No quarterly budget is recorded for this quarter yet.' : 'No funding plan yet.'}{' '}
            <button type="button" className="text-primary-700 font-medium hover:underline" onClick={() => onSection(plan ? 'money' : 'funding')}>{plan ? 'Set up the budget' : 'Add funding'}</button>
          </Panel>
        )}
        <NeedsDoing items={file.attention} onSection={onSection} />
        <Panel title="Visits" icon={CalendarDays} actions={<button type="button" className="btn-ghost btn-sm" onClick={() => onSection('visits')}>All visits <ChevronRight size={14} /></button>} bodyClass="divide-y divide-slate-100">
          <VisitList title="Coming up" rows={file.visits.upcoming} empty="Nothing booked." />
          <VisitList title="Recently" rows={file.visits.recent.slice(0, 3)} empty="No visits yet." />
        </Panel>
      </div>
      <div className="space-y-4">
        <Panel title="Statements" icon={FileText} actions={<button type="button" className="btn-ghost btn-sm" onClick={() => onSection('money')}>Open <ChevronRight size={14} /></button>} bodyClass="divide-y divide-slate-100">
          {file.statements.length === 0 && <p className="px-4 py-3 text-[13px] text-slate-500">No statements yet.</p>}
          {file.statements.slice(0, 4).map((s) => (
            <div key={s.id} className="px-4 py-2.5 flex items-center justify-between gap-3 text-[13px]">
              <span className="text-slate-800">{monthLabel(s.period)}</span>
              <span className="flex items-center gap-2"><span className="tabular-nums text-slate-600">{money(s.spent)}</span><AcStatus status={s.status} /></span>
            </div>
          ))}
        </Panel>
        <Panel title="Care" icon={ClipboardList} bodyClass="px-4 py-3 space-y-3 text-[13px]">
          <Fact label="Care plan" onClick={() => onSection('careplan')}
            value={file.carePlan ? <span className="flex items-center gap-2"><AcStatus status={file.carePlan.status} />{file.carePlan.services} service{file.carePlan.services === 1 ? '' : 's'}</span> : <span className="text-amber-700">None yet</span>} />
          {file.carePlan && <Fact label="Review" value={<Deadline date={file.carePlan.review_due} absolute={false} />} />}
          <Fact label="Care management this month" onClick={() => onSection('caremgmt')}
            value={<span className={clsx('tabular-nums', file.careManagement && file.careManagement.direct ? 'text-slate-800' : 'text-amber-700')}>
              {file.careManagement ? `${file.careManagement.minutes} min, ${file.careManagement.direct} direct` : 'None'}
            </span>} />
          {file.athm.map((a) => (
            <Fact key={a.id} label={a.tier || 'AT-HM'} value={<span className="tabular-nums">{money(a.amount - a.spent)} left{a.expires_on ? `, ends ${fmtDate(a.expires_on)}` : ''}</span>} />
          ))}
        </Panel>
        <Panel title="Contact" icon={Phone} bodyClass="px-4 py-3 space-y-2 text-[13px]">
          <ContactLine icon={Mail} value={file.client.contact && file.client.contact.email} missing="No email: statements cannot be emailed" />
          <ContactLine icon={Phone} value={(file.client.contact && file.client.contact.phone) || file.client.mobile} missing="No phone" />
          <ContactLine icon={MapPin} value={[file.client.address, file.client.suburb, file.client.state, file.client.postcode].filter(Boolean).join(', ')} missing="No address" />
          {file.supporters.length > 0 && (
            <div className="pt-2 border-t border-slate-100">
              <p className="text-[12px] text-slate-500 mb-1">Supporters</p>
              {file.supporters.map((s) => <p key={s.id} className="text-slate-700">{s.name}{s.relationship ? <span className="text-slate-500">, {s.relationship}</span> : null}{s.decision_maker ? <span className="text-slate-500"> (decision maker)</span> : null}</p>)}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

// The signature of the file: what is left this quarter, spending split by category, and a mark
// for where spending would be if it kept pace with the quarter.
function QuarterMoney({ q, onOpen }) {
  const st = STATE[q.state] || STATE.on_track;
  const pool = q.pool > 0 ? q.pool : 0;
  const parts = ['clinical', 'independence', 'everyday'].map((k) => ({ k, v: Number((q.byCategory || {})[k] || 0) }));
  const pct = (v) => (pool > 0 ? Math.min(100, (v / pool) * 100) : 0);
  return (
    <Panel title={`This quarter, to ${fmtDate(q.end)}`} icon={Wallet}
      actions={<span className={clsx('rounded-md px-2 py-0.5 text-[12px] font-medium ring-1', st.chip)}>{st.word}</span>} bodyClass="px-5 pt-4 pb-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className={clsx('font-display text-[34px] leading-none font-semibold tabular-nums tracking-[-0.02em]', q.remaining < 0 ? 'text-red-800' : 'text-slate-900')}>{money(q.remaining)}</div>
          <p className="text-[13px] text-slate-500 mt-1.5">left for services, of {money(q.pool)}{q.carry > 0 ? ` (with ${money(q.carry)} carried over)` : ''}</p>
        </div>
        <div className="text-right">
          <div className="font-display text-[20px] font-semibold tabular-nums text-slate-800">{q.daysLeft}</div>
          <p className="text-[12px] text-slate-500">days left in the quarter</p>
        </div>
      </div>
      <div className="relative mt-4">
        <div className="h-3 rounded-full bg-slate-100 overflow-hidden flex" role="img"
          aria-label={`${money(q.spent)} used of ${money(q.pool)}; ${Math.round(q.pace * 100)}% of the quarter has gone`}>
          {parts.map((p) => p.v > 0 && <div key={p.k} className={CATEGORY_HUE[p.k].bar} style={{ width: `${pct(p.v)}%` }} />)}
        </div>
        <span className="absolute -top-1 h-5 w-0.5 rounded bg-slate-700" style={{ left: `calc(${Math.min(100, q.pace * 100)}% - 1px)` }} aria-hidden="true" />
      </div>
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 text-[12px] text-slate-500">
        <span className="flex flex-wrap gap-x-3 gap-y-1">
          {parts.map((p) => (
            <span key={p.k} className="inline-flex items-center gap-1.5">
              <span className={clsx('w-2 h-2 rounded-sm', CATEGORY_HUE[p.k].bar)} aria-hidden="true" />{CATEGORY_HUE[p.k].label} <span className="tabular-nums text-slate-700">{money(p.v)}</span>
            </span>
          ))}
        </span>
        <span className="inline-flex items-center gap-1.5"><span className="w-0.5 h-3 bg-slate-700 rounded" aria-hidden="true" />Where spending would be at an even pace</span>
      </div>
      <button type="button" onClick={onOpen} className="mt-3 text-[13px] font-medium text-primary-700 hover:underline">Budgets and statements</button>
    </Panel>
  );
}

function NeedsDoing({ items, onSection }) {
  if (!items || items.length === 0) {
    return (
      <Panel bodyClass="px-4 py-3 flex items-center gap-2 text-[13px] text-slate-600">
        <CheckCircle2 size={16} className="text-emerald-600" aria-hidden="true" /> Nothing needs doing for them right now.
      </Panel>
    );
  }
  return (
    <Panel title={`Needs doing (${items.length})`} icon={AlertCircle} bodyClass="divide-y divide-slate-100">
      {items.map((it) => {
        const [Icon, cls] = MARK[it.severity] || [Clock, 'text-slate-400'];
        const target = TAB_OF[it.tab];
        return (
          <button key={it.id} type="button" disabled={!target} onClick={() => target && onSection(target)}
            className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:bg-slate-50">
            <Icon size={17} className={clsx('mt-0.5 shrink-0', cls)} aria-hidden="true" />
            <span className="flex-1 min-w-0">
              <span className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className="text-[13.5px] font-medium text-slate-900">{it.title}</span>
                {it.due && <Deadline date={it.due} absolute={false} className="text-[12px]" />}
              </span>
              {it.detail && <span className="block text-[12.5px] text-slate-500 mt-0.5">{it.detail}</span>}
            </span>
          </button>
        );
      })}
    </Panel>
  );
}

function VisitList({ title, rows, empty }) {
  return (
    <div className="px-4 py-3">
      <p className="text-[12px] text-slate-500 mb-1.5">{title}</p>
      {rows.length === 0 && <p className="text-[13px] text-slate-500">{empty}</p>}
      <ul className="space-y-1.5">
        {rows.map((v) => (
          <li key={v.id} className="flex flex-wrap items-baseline justify-between gap-x-3 text-[13px]">
            <span className="text-slate-800"><span className="tabular-nums">{fmtDate(v.date)}, {v.start} to {v.end}</span>{v.service ? <span className="text-slate-500">, {v.service}</span> : null}</span>
            <span className="text-slate-500">{v.worker || 'No worker yet'}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Visits({ file }) {
  return (
    <Panel title="Visits" icon={CalendarDays} actions={<Link to="/scheduler" className="btn-secondary btn-sm"><CalendarPlus size={14} /> Open the scheduler</Link>} bodyClass="divide-y divide-slate-100">
      <VisitList title="Coming up" rows={file.visits.upcoming} empty="Nothing booked." />
      <VisitList title="Recently" rows={file.visits.recent} empty="No visits yet." />
      <div className="px-4 py-3 text-[12.5px] text-slate-500">Visits are booked and changed on the scheduler, like every other client. Leave and hospital stays are below.</div>
      <div className="p-4"><AgedCareLeave clientId={file.client.id} embedded /></div>
    </Panel>
  );
}

function Fact({ label, value, onClick }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      {onClick
        ? <button type="button" onClick={onClick} className="text-slate-600 hover:text-primary-700 hover:underline text-left">{label}</button>
        : <span className="text-slate-600">{label}</span>}
      <span className="text-right">{value}</span>
    </div>
  );
}
function ContactLine({ icon: Icon, value, missing }) {
  return (
    <p className="flex items-start gap-2">
      <Icon size={14} className="mt-0.5 text-slate-400 shrink-0" aria-hidden="true" />
      {value ? <span className="text-slate-700 break-words">{value}</span> : <span className="text-amber-700">{missing}</span>}
    </p>
  );
}

// ── Edit the participant's details ──────────────────────────────────────────────────────────────
const STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
function EditDetails({ client, onClose, onSaved }) {
  const initial = useMemo(() => ({
    title: client.title || '', first_name: client.first_name || '', last_name: client.last_name || '', preferred_name: client.preferred_name || '',
    date_of_birth: client.date_of_birth || '', gender: client.gender || '',
    email: (client.contact && client.contact.email) || '', phone: (client.contact && client.contact.phone) || client.mobile || '',
    address: client.address || '', suburb: client.suburb || '', state: client.state || '', postcode: client.postcode || '',
    aged_care_recipient_id: client.mac || '', sa_care_recipient_id: client.sa_id || '',
    cultural_requirements: client.cultural_requirements || '', atsi_status: client.atsi_status || '',
  }), [client]);
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => { const v = e.target.value; setF((p) => ({ ...p, [k]: v })); };
  const save = async () => {
    const changed = Object.fromEntries(Object.entries(f).filter(([k, v]) => v !== initial[k]));
    if (!Object.keys(changed).length) { onClose(); return; }
    setSaving(true);
    try { await api.patch(`/aged-care/clients/${client.id}`, changed); toast.success('Details saved'); onSaved(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save the details'); }
    finally { setSaving(false); }
  };
  const close = useCallback(() => onClose(), [onClose]);
  return (
    <DetailDrawer open onClose={close} icon={Pencil} width="lg" title={`Edit ${client.name}`}
      footer={<div className="flex justify-end gap-2"><button type="button" className="btn-secondary btn-sm" onClick={close}>Cancel</button><button type="button" className="btn-primary btn-sm" disabled={saving || !f.first_name.trim() || !f.last_name.trim()} onClick={save}>{saving ? 'Saving' : 'Save'}</button></div>}>
      <div className="space-y-5">
        <section className="space-y-3">
          <h3 className="text-[13px] font-semibold text-slate-800">Name</h3>
          <FormRow cols={4}>
            <Field label="Title">{(id) => <input id={id} className="input-field" value={f.title} onChange={set('title')} />}</Field>
            <Field label="First name" required>{(id) => <input id={id} className="input-field" value={f.first_name} onChange={set('first_name')} />}</Field>
            <Field label="Last name" required>{(id) => <input id={id} className="input-field" value={f.last_name} onChange={set('last_name')} />}</Field>
            <Field label="Goes by">{(id) => <input id={id} className="input-field" value={f.preferred_name} onChange={set('preferred_name')} />}</Field>
          </FormRow>
          <FormRow cols={2}>
            <Field label="Date of birth">{(id) => <input id={id} type="date" className="input-field" value={f.date_of_birth} onChange={set('date_of_birth')} />}</Field>
            <Field label="Gender">{(id) => <input id={id} className="input-field" value={f.gender} onChange={set('gender')} />}</Field>
          </FormRow>
        </section>
        <section className="space-y-3">
          <h3 className="text-[13px] font-semibold text-slate-800">Contact</h3>
          <FormRow cols={2}>
            <Field label="Email" hint="Their monthly statement is emailed here.">{(id) => <input id={id} type="email" className="input-field" value={f.email} onChange={set('email')} />}</Field>
            <Field label="Phone">{(id) => <input id={id} type="tel" className="input-field" value={f.phone} onChange={set('phone')} />}</Field>
          </FormRow>
          <Field label="Street address">{(id) => <input id={id} className="input-field" value={f.address} onChange={set('address')} />}</Field>
          <FormRow cols={3}>
            <Field label="Suburb">{(id) => <input id={id} className="input-field" value={f.suburb} onChange={set('suburb')} />}</Field>
            <Field label="State">{(id) => <select id={id} className="input-field" value={f.state} onChange={set('state')}><option value="">Choose</option>{STATES.map((s) => <option key={s} value={s}>{s}</option>)}</select>}</Field>
            <Field label="Postcode">{(id) => <input id={id} inputMode="numeric" className="input-field" value={f.postcode} onChange={set('postcode')} />}</Field>
          </FormRow>
        </section>
        <section className="space-y-3">
          <h3 className="text-[13px] font-semibold text-slate-800">Identifiers</h3>
          <FormRow cols={2}>
            <Field label="My Aged Care ID" hint="AC followed by 8 digits.">{(id) => <input id={id} className="input-field font-mono" value={f.aged_care_recipient_id} onChange={set('aged_care_recipient_id')} placeholder="AC12345678" />}</Field>
            <Field label="Services Australia care recipient ID" hint="9 digits. Every claim line needs it.">{(id) => <input id={id} inputMode="numeric" className="input-field font-mono" value={f.sa_care_recipient_id} onChange={set('sa_care_recipient_id')} />}</Field>
          </FormRow>
        </section>
        <section className="space-y-3">
          <h3 className="text-[13px] font-semibold text-slate-800">Background</h3>
          <FormRow cols={2}>
            <Field label="Aboriginal or Torres Strait Islander">{(id) => (
              <select id={id} className="input-field" value={f.atsi_status} onChange={set('atsi_status')}>
                <option value="">Not stated</option>
                {['Aboriginal', 'Torres Strait Islander', 'Both Aboriginal and Torres Strait Islander', 'Neither'].map((o) => <option key={o} value={o}>{o}</option>)}
                {f.atsi_status && !['Aboriginal', 'Torres Strait Islander', 'Both Aboriginal and Torres Strait Islander', 'Neither'].includes(f.atsi_status) && <option value={f.atsi_status}>{f.atsi_status}</option>}
              </select>
            )}</Field>
          </FormRow>
          <Field label="Cultural and language needs">{(id) => <textarea id={id} rows={2} className="input-field" value={f.cultural_requirements} onChange={set('cultural_requirements')} />}</Field>
        </section>
      </div>
    </DetailDrawer>
  );
}
